import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from './ui/card';
import { Button } from './ui/button';
import { Badge } from './ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from './ui/dialog';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Switch } from './ui/switch';
import { CalendarOff, Plus, Trash2, Users, Shield, Tag, AlertTriangle } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const emptyForm = { label: '', start_date: '', end_date: '', pto_types: [], team_ids: [], roles: [] };

const PTOBlackouts = () => {
  const [blackouts, setBlackouts] = useState([]);
  const [teams, setTeams] = useState([]);
  const [roles, setRoles] = useState([]);
  const [ptoTypes, setPtoTypes] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

  useEffect(() => {
    fetchAll();
  }, []);

  const fetchAll = async () => {
    try {
      const headers = authHeaders();
      const [bRes, tRes, rRes, pRes] = await Promise.all([
        axios.get(`${API}/pto-blackouts?include_inactive=true`, { headers }),
        axios.get(`${API}/teams`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/role-permissions`, { headers }).catch(() => ({ data: [] })),
        axios.get(`${API}/pto-types`, { headers }).catch(() => ({ data: [] })),
      ]);
      setBlackouts(bRes.data || []);
      setTeams(tRes.data || []);
      setRoles(rRes.data || []);
      setPtoTypes(pRes.data || []);
    } catch (error) {
      console.error('Failed to load blackouts:', error);
      toast.error('Failed to load blackout dates');
    }
  };

  const toggleInList = (key, value) => {
    setForm((prev) => {
      const arr = prev[key];
      return { ...prev, [key]: arr.includes(value) ? arr.filter((v) => v !== value) : [...arr, value] };
    });
  };

  const openCreate = () => {
    setForm(emptyForm);
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!form.label.trim()) { toast.error('Please enter a label'); return; }
    if (!form.start_date || !form.end_date) { toast.error('Please select a start and end date'); return; }
    if (form.end_date < form.start_date) { toast.error('End date must be on or after the start date'); return; }
    setSaving(true);
    try {
      await axios.post(`${API}/pto-blackouts`, form, { headers: authHeaders() });
      toast.success('Blackout created');
      setShowModal(false);
      setForm(emptyForm);
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create blackout');
    } finally {
      setSaving(false);
    }
  };

  const handleToggleActive = async (b) => {
    try {
      await axios.put(`${API}/pto-blackouts/${b.id}`, { is_active: !b.is_active }, { headers: authHeaders() });
      toast.success(b.is_active ? 'Blackout disabled' : 'Blackout enabled');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update blackout');
    }
  };

  const handleDelete = async (b) => {
    if (!window.confirm(`Delete blackout "${b.label}"?`)) return;
    try {
      await axios.delete(`${API}/pto-blackouts/${b.id}`, { headers: authHeaders() });
      toast.success('Blackout deleted');
      fetchAll();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete blackout');
    }
  };

  const fmtDate = (d) => {
    if (!d) return '';
    const [y, m, day] = d.split('-').map(Number);
    return new Date(y, m - 1, day).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const teamName = (id) => teams.find((t) => t.id === id)?.name || id;
  const ptoLabel = (val) => ptoTypes.find((p) => p.value === val)?.display_name || val;
  const roleLabel = (r) => r.charAt(0).toUpperCase() + r.slice(1).replace(/_/g, ' ');

  return (
    <div className="space-y-6" data-testid="pto-blackouts-tab">
      <Card className="shadow-sm border-0">
        <CardHeader className="flex flex-row items-center justify-between space-y-0">
          <div>
            <CardTitle className="flex items-center gap-2">
              <CalendarOff className="h-5 w-5 text-rose-600" />
              PTO Blackout Dates
            </CardTitle>
            <CardDescription className="mt-1">
              Close off dates so new time-off requests are flagged for manager review. Existing approved time off is not affected.
            </CardDescription>
          </div>
          <Button onClick={openCreate} data-testid="add-blackout-btn" className="bg-rose-600 hover:bg-rose-700">
            <Plus className="h-4 w-4 mr-1" /> Add Blackout
          </Button>
        </CardHeader>
        <CardContent>
          {blackouts.length === 0 ? (
            <div className="text-center py-12 text-gray-500" data-testid="blackouts-empty">
              <CalendarOff className="h-12 w-12 mx-auto mb-3 text-gray-300" />
              <p className="font-medium">No blackout dates yet</p>
              <p className="text-sm">Add a blackout to flag PTO requests during closed periods.</p>
            </div>
          ) : (
            <div className="space-y-3" data-testid="blackouts-list">
              {blackouts.map((b) => (
                <div
                  key={b.id}
                  data-testid={`blackout-row-${b.id}`}
                  className={`flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 rounded-xl border ${
                    b.is_active ? 'bg-rose-50/60 border-rose-200' : 'bg-gray-50 border-gray-200 opacity-70'
                  }`}
                >
                  <div className="min-w-0 space-y-1.5">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-semibold text-gray-900">{b.label}</span>
                      <Badge variant="outline" className="text-rose-700 border-rose-300 bg-white">
                        {fmtDate(b.start_date)} &ndash; {fmtDate(b.end_date)}
                      </Badge>
                      {!b.is_active && <Badge variant="secondary">Disabled</Badge>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap text-xs text-gray-600">
                      <span className="inline-flex items-center gap-1">
                        <Tag className="h-3.5 w-3.5" />
                        {(!b.pto_types || b.pto_types.length === 0) ? 'All PTO types' : b.pto_types.map(ptoLabel).join(', ')}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="inline-flex items-center gap-1">
                        <Users className="h-3.5 w-3.5" />
                        {(!b.team_ids || b.team_ids.length === 0) ? 'All teams' : b.team_ids.map(teamName).join(', ')}
                      </span>
                      <span className="text-gray-300">|</span>
                      <span className="inline-flex items-center gap-1">
                        <Shield className="h-3.5 w-3.5" />
                        {(!b.roles || b.roles.length === 0) ? 'All roles' : b.roles.map(roleLabel).join(', ')}
                      </span>
                    </div>
                  </div>
                  <div className="flex items-center gap-3 shrink-0">
                    <div className="flex items-center gap-2">
                      <Switch
                        checked={b.is_active}
                        onCheckedChange={() => handleToggleActive(b)}
                        data-testid={`blackout-toggle-${b.id}`}
                      />
                      <span className="text-xs text-gray-500 w-12">{b.is_active ? 'Active' : 'Off'}</span>
                    </div>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(b)}
                      data-testid={`blackout-delete-${b.id}`}
                      className="text-gray-400 hover:text-rose-600"
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={showModal} onOpenChange={setShowModal}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto" data-testid="blackout-modal">
          <DialogHeader>
            <DialogTitle>Add Blackout Dates</DialogTitle>
            <DialogDescription>
              PTO requests overlapping these dates will be allowed but flagged with a warning for manager review.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-2">
            <div>
              <Label htmlFor="bo-label">Label / Reason</Label>
              <Input
                id="bo-label"
                data-testid="blackout-label-input"
                placeholder="e.g. Year-End Close, Peak Season"
                value={form.label}
                onChange={(e) => setForm({ ...form, label: e.target.value })}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label htmlFor="bo-start">Start date</Label>
                <Input
                  id="bo-start"
                  type="date"
                  data-testid="blackout-start-input"
                  value={form.start_date}
                  onChange={(e) => setForm({ ...form, start_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="bo-end">End date</Label>
                <Input
                  id="bo-end"
                  type="date"
                  data-testid="blackout-end-input"
                  value={form.end_date}
                  onChange={(e) => setForm({ ...form, end_date: e.target.value })}
                />
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">PTO types</Label>
              <p className="text-xs text-gray-500 mb-2">Leave all unchecked to block every type.</p>
              <div className="flex flex-wrap gap-2">
                {ptoTypes.length === 0 && <span className="text-xs text-gray-400">No PTO types configured</span>}
                {ptoTypes.map((p) => (
                  <button
                    type="button"
                    key={p.value}
                    onClick={() => toggleInList('pto_types', p.value)}
                    data-testid={`blackout-ptotype-${p.value}`}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      form.pto_types.includes(p.value)
                        ? 'bg-rose-600 text-white border-rose-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-rose-300'
                    }`}
                  >
                    {p.display_name || p.value}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Teams</Label>
              <p className="text-xs text-gray-500 mb-2">Leave all unchecked to apply org-wide.</p>
              <div className="flex flex-wrap gap-2">
                {teams.length === 0 && <span className="text-xs text-gray-400">No teams created</span>}
                {teams.map((t) => (
                  <button
                    type="button"
                    key={t.id}
                    onClick={() => toggleInList('team_ids', t.id)}
                    data-testid={`blackout-team-${t.id}`}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      form.team_ids.includes(t.id)
                        ? 'bg-blue-600 text-white border-blue-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-blue-300'
                    }`}
                  >
                    {t.name}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <Label className="mb-1.5 block">Roles</Label>
              <p className="text-xs text-gray-500 mb-2">Leave all unchecked to apply to every role.</p>
              <div className="flex flex-wrap gap-2">
                {roles.length === 0 && <span className="text-xs text-gray-400">No roles configured</span>}
                {roles.map((r) => (
                  <button
                    type="button"
                    key={r.role}
                    onClick={() => toggleInList('roles', r.role)}
                    data-testid={`blackout-role-${r.role}`}
                    className={`px-3 py-1 rounded-full text-sm border transition-colors ${
                      form.roles.includes(r.role)
                        ? 'bg-purple-600 text-white border-purple-600'
                        : 'bg-white text-gray-700 border-gray-300 hover:border-purple-300'
                    }`}
                  >
                    {roleLabel(r.role)}
                  </button>
                ))}
              </div>
            </div>

            <div className="flex items-start gap-2 p-3 rounded-lg bg-amber-50 border border-amber-200 text-xs text-amber-800">
              <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>Employees can still submit requests during a blackout, but they will be clearly warned and the request is routed to a manager for review. Admins/managers can still add PTO manually.</span>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowModal(false)} disabled={saving}>Cancel</Button>
            <Button onClick={handleSave} disabled={saving} data-testid="blackout-save-btn" className="bg-rose-600 hover:bg-rose-700">
              {saving ? 'Saving...' : 'Create Blackout'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PTOBlackouts;
